import { CommonModule } from "@angular/common";
import { Component, Input, forwardRef } from "@angular/core";
import {
  AbstractControl,
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from "@angular/forms";
import { FormErrorComponent } from "../form-error/form-error.component";

export type InputType =
  | "text"
  | "email"
  | "password"
  | "number"
  | "tel"
  | "url"
  | "search"
  | "date"
  | "datetime-local"
  | "month"
  | "time";

@Component({
  selector: "is-input-field",
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, FormErrorComponent],
  templateUrl: "./input-field.component.html",
  styleUrls: ["./input-field.component.scss"],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputFieldComponent),
      multi: true,
    },
  ],
})
export class InputFieldComponent implements ControlValueAccessor {
  @Input() label = "";
  @Input() placeholder = "";
  @Input() type: InputType = "text";
  @Input() helpText = "";
  @Input() required = false;
  @Input() readonly = false;
  @Input() prefixIcon = "";
  @Input() suffixIcon = "";
  @Input() control: AbstractControl | null = null;
  @Input() min: number | null = null;
  @Input() max: number | null = null;

  value = "";
  isDisabled = false;
  showPassword = false;

  private onChange: (val: string) => void = () => {};
  private onTouched: () => void = () => {};

  get inputType(): string {
    if (this.type === "password") {
      return this.showPassword ? "text" : "password";
    }
    return this.type;
  }

  get hasError(): boolean {
    return !!(
      this.control?.invalid &&
      (this.control?.dirty || this.control?.touched)
    );
  }

  get inputClasses(): Record<string, boolean> {
    return {
      "form-control": true,
      "is-invalid": this.hasError,
      "ps-5": !!this.prefixIcon,
    };
  }

  writeValue(val: string): void {
    this.value = val ?? "";
  }

  registerOnChange(fn: (val: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.isDisabled = disabled;
  }

  handleInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const val = input.value;
    this.value = val;
    this.onChange(val);
  }

  handleBlur(): void {
    if (this.type === "number" && this.value !== "") {
      const num = Number(this.value);
      if (!isNaN(num)) {
        let clamped = num;
        if (this.max !== null && num > this.max) clamped = this.max;
        if (this.min !== null && num < this.min) clamped = this.min;
        if (clamped !== num) {
          this.value = String(clamped);
          this.onChange(this.value);
        }
      }
    }
    this.onTouched();
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }
}
